import { useContext } from 'react'

import { Box, Button, ButtonGroup, Grid, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'

import { isDisplayParentYearMetricsEnabled } from 'store/feature-toggle'

import PremiumInsightsContext from '../PremiumInsightsProvider/Context'
import { METRIC_TITLES, METRIC_TOOLTIPS, PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS } from '../constants'
import InsightsMetric, {
  NullDelta,
  SecondaryDeltaPercentage,
  SecondaryValue,
  formatPercentage,
  formatTotal,
} from './InsightsMetric'

const useStyles = makeStyles((theme) => {
  return {
    selected: {
      backgroundColor: `rgba(0, 125, 170, 0.08)`,
      color: theme.palette.primary.dark,
      '&:hover': {
        backgroundColor: `rgba(0, 125, 170, 0.08)`,
      },
    },
  }
})

export default function InsightsEngagement() {
  const classes = useStyles()
  const { dispatch, getCollegePremiumInsightsRequest, selectedFilters, selectedEngagementMetricType } =
    useContext(PremiumInsightsContext)

  const isParentAudience = selectedFilters.audience === 'parent' || selectedFilters.audience === 'all'
  const isAcademicYearFilter = selectedFilters.bucket === '1y'
  const displayParentYearMetrics = useSelector((state) => isDisplayParentYearMetricsEnabled(state))

  const hideParentDelta = !displayParentYearMetrics && isParentAudience && isAcademicYearFilter

  let formattedDate = ''
  if (getCollegePremiumInsightsRequest.status === 'success') {
    formattedDate = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'short',
      timeStyle: 'long',
    }).format(new Date(getCollegePremiumInsightsRequest.data.updatedAt))
  }
  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        alignItems="center"
        gridGap={8}
        mb={2}
      >
        <Typography
          variant="subtitle2"
          component="h3"
        >
          Engagement
        </Typography>
        <Box>
          <ButtonGroup
            color="primary"
            disableRipple
          >
            <Button
              className={selectedEngagementMetricType === 'count' ? classes.selected : undefined}
              onClick={() => {
                dispatch({ type: 'SELECT_ENGAGEMENT_METRIC_TYPE', payload: 'count' })
              }}
            >
              Count
            </Button>
            <Button
              className={selectedEngagementMetricType === 'rate' ? classes.selected : undefined}
              onClick={() => {
                dispatch({ type: 'SELECT_ENGAGEMENT_METRIC_TYPE', payload: 'rate' })
              }}
            >
              Conversion Rate
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
      <Grid
        container
        spacing={2}
      >
        {getCollegePremiumInsightsRequest.status === 'success' && (
          <>
            <Grid
              item
              md={4}
              xs={12}
            >
              {selectedEngagementMetricType === 'count' ? (
                <InsightsMetric
                  title={METRIC_TITLES.TOTAL_ENGAGEMENT_COUNT}
                  tooltipText={METRIC_TOOLTIPS.TOTAL_ENGAGEMENT_COUNT}
                  primaryValue={
                    <Typography
                      variant="h5"
                      component="div"
                    >
                      {formatTotal(getCollegePremiumInsightsRequest.data.countMetrics.totalEngagement.count)}
                    </Typography>
                  }
                  secondaryValue={
                    hideParentDelta ? (
                      <NullDelta
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                        isPercentage
                      />
                    ) : (
                      <SecondaryDeltaPercentage
                        percentage={
                          getCollegePremiumInsightsRequest.data.countMetrics.totalEngagement.countPercentChange
                        }
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      />
                    )
                  }
                />
              ) : (
                <InsightsMetric
                  title={METRIC_TITLES.TOTAL_ENGAGEMENT_RATE}
                  tooltipText={METRIC_TOOLTIPS.TOTAL_ENGAGEMENT_RATE}
                  primaryValue={
                    <Typography
                      variant="h5"
                      component="div"
                    >
                      {formatPercentage(getCollegePremiumInsightsRequest.data.rateMetrics.totalEngagement.rate)}
                    </Typography>
                  }
                  secondaryValue={
                    hideParentDelta ? (
                      <NullDelta label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]} />
                    ) : (
                      <SecondaryValue
                        value={formatPercentage(
                          getCollegePremiumInsightsRequest.data.rateMetrics.totalEngagement.ratePrev,
                        )}
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      />
                    )
                  }
                />
              )}
            </Grid>
            <Grid
              item
              md={4}
              xs={12}
            >
              {selectedEngagementMetricType === 'count' ? (
                <InsightsMetric
                  title={METRIC_TITLES.PROFILE_VISITS_COUNT}
                  tooltipText={METRIC_TOOLTIPS.PROFILE_VISITS_COUNT}
                  primaryValue={
                    <Typography
                      variant="h5"
                      component="div"
                    >
                      {formatTotal(getCollegePremiumInsightsRequest.data.countMetrics.profileVisits.count)}
                    </Typography>
                  }
                  secondaryValue={
                    hideParentDelta ? (
                      <NullDelta
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                        isPercentage
                      />
                    ) : (
                      <SecondaryDeltaPercentage
                        percentage={getCollegePremiumInsightsRequest.data.countMetrics.profileVisits.countPercentChange}
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      />
                    )
                  }
                />
              ) : (
                <InsightsMetric
                  title={METRIC_TITLES.PROFILE_VISITS_RATE}
                  tooltipText={METRIC_TOOLTIPS.PROFILE_VISITS_RATE}
                  primaryValue={
                    <Typography
                      variant="h5"
                      component="div"
                    >
                      {formatPercentage(getCollegePremiumInsightsRequest.data.rateMetrics.profileVisits.rate)}
                    </Typography>
                  }
                  secondaryValue={
                    hideParentDelta ? (
                      <NullDelta label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]} />
                    ) : (
                      <SecondaryValue
                        value={formatPercentage(
                          getCollegePremiumInsightsRequest.data.rateMetrics.profileVisits.ratePrev,
                        )}
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      />
                    )
                  }
                />
              )}
            </Grid>
            <Grid
              item
              md={4}
              xs={12}
            >
              {selectedEngagementMetricType === 'count' ? (
                <InsightsMetric
                  title={METRIC_TITLES.REFERRAL_TRAFFIC_COUNT}
                  tooltipText={METRIC_TOOLTIPS.REFERRAL_TRAFFIC_COUNT}
                  primaryValue={
                    <Typography
                      variant="h5"
                      component="div"
                    >
                      {formatTotal(getCollegePremiumInsightsRequest.data.countMetrics.referredTraffic.count)}
                    </Typography>
                  }
                  secondaryValue={
                    hideParentDelta ? (
                      <NullDelta
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                        isPercentage
                      />
                    ) : (
                      <SecondaryDeltaPercentage
                        percentage={
                          getCollegePremiumInsightsRequest.data.countMetrics.referredTraffic.countPercentChange
                        }
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      />
                    )
                  }
                />
              ) : (
                <InsightsMetric
                  title={METRIC_TITLES.REFERRAL_TRAFFIC_RATE}
                  tooltipText={METRIC_TOOLTIPS.REFERRAL_TRAFFIC_RATE}
                  primaryValue={
                    <Typography
                      variant="h5"
                      component="div"
                    >
                      {formatPercentage(getCollegePremiumInsightsRequest.data.rateMetrics.referredTraffic.rate)}
                    </Typography>
                  }
                  secondaryValue={
                    hideParentDelta ? (
                      <NullDelta label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]} />
                    ) : (
                      <SecondaryValue
                        value={formatPercentage(
                          getCollegePremiumInsightsRequest.data.rateMetrics.referredTraffic.ratePrev,
                        )}
                        label={PREVIOUS_DATE_RANGE_METRIC_DESCRIPTORS[selectedFilters.bucket]}
                      />
                    )
                  }
                />
              )}
            </Grid>
            <Grid
              item
              xs={12}
            >
              <Typography
                color="textPrimary"
                variant="caption"
              >
                {`Last updated ${formattedDate}`}
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  )
}
